import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Upload, Loader2, Trash2, Eye, EyeOff, Database, BarChart3, DollarSign,
  Users, MapPin, TrendingUp, ChevronLeft, ChevronRight, Search, FileSpreadsheet, Calendar, Edit
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

export default function AdminImportedDataPage() {
  const { api } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('insights');

  // Edit state
  const [editingCell, setEditingCell] = useState(null); // {rowId, field, value}
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);
  const [editDialogType, setEditDialogType] = useState(''); // 'transaction' or 'booking'

  // Data table state
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsSearch, setRowsSearch] = useState('');
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnLabels, setColumnLabels] = useState({});
  const [loadingRows, setLoadingRows] = useState(false);

  // Insights state
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsSearch, setBookingsSearch] = useState('');
  const [bookingsFilter, setBookingsFilter] = useState('all');
  const [bookingsStatFilter, setBookingsStatFilter] = useState('');
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsInsights, setBookingsInsights] = useState(null);

  // Column management
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnManager, setShowColumnManager] = useState(false);

  useEffect(() => { fetchDatasets(); }, []);

  const fetchDatasets = async () => {
    try {
      const res = await api.get('/imported-data/');
      setDatasets(res.data);
      if (res.data.length > 0 && !selectedDataset) {
        selectDataset(res.data[0]);
      }
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const selectDataset = useCallback(async (ds) => {
    setSelectedDataset(ds);
    setHiddenColumns(ds.hidden_columns || []);
    setRowsPage(1);
    setRowsSearch('');
    // Determine dataset type by checking if it has bookings
    const hasBookings = ds.file_name?.includes('.xlsx') || ds.name?.toLowerCase().includes('booking');
    fetchRows(ds.id, 1, '');
    fetchInsights(ds.id);
    if (hasBookings) {
      fetchBookings(ds.id, 1, '', 'all');
      fetchBookingsInsights(ds.id);
    }
  }, []);

  const fetchRows = async (dsId, page = 1, search = '') => {
    setLoadingRows(true);
    try {
      const res = await api.get(`/imported-data/${dsId}/rows`, { params: { page, page_size: 50, search } });
      setRows(res.data.rows);
      setRowsTotal(res.data.total);
      setVisibleColumns(res.data.visible_columns);
      setColumnLabels(res.data.column_labels);
    } catch (err) { console.error(err); }
    finally { setLoadingRows(false); }
  };

  const fetchInsights = async (dsId) => {
    setLoadingInsights(true);
    try {
      const res = await api.get(`/imported-data/${dsId}/insights`);
      setInsights(res.data);
    } catch (err) { /* Transaction insights may not exist for bookings */ }
    finally { setLoadingInsights(false); }
  };

  const fetchBookings = async (dsId, page = 1, search = '', recordType = 'all', statFilter = '') => {
    setLoadingBookings(true);
    try {
      const res = await api.get(`/imported-data/${dsId}/bookings`, { params: { page, page_size: 50, search, record_type: recordType, filter: statFilter } });
      setBookings(res.data.rows);
      setBookingsTotal(res.data.total);
    } catch (err) { setBookings([]); setBookingsTotal(0); }
    finally { setLoadingBookings(false); }
  };

  const fetchBookingsInsights = async (dsId) => {
    try {
      const res = await api.get(`/imported-data/${dsId}/bookings-insights`);
      setBookingsInsights(res.data);
    } catch (err) { setBookingsInsights(null); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name.replace('.csv', '').replace(/_/g, ' '));
      const res = await api.post('/imported-data/upload', fd);
      toast.success(`Imported ${res.data.imported} rows!`);
      fetchDatasets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleBookingsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name.replace('.xlsx', '').replace('.xls', '').replace(/_/g, ' '));
      const res = await api.post('/imported-data/upload-bookings', fd);
      toast.success(`Imported ${res.data.unique_imported} unique records from ${res.data.total_raw_rows} rows (${res.data.duplicates_removed} duplicates removed)!`);
      fetchDatasets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDataset = async (dsId) => {
    if (!window.confirm('Delete this entire dataset and all its data?')) return;
    try {
      await api.delete(`/imported-data/${dsId}`);
      toast.success('Dataset deleted');
      setSelectedDataset(null);
      setInsights(null);
      setRows([]);
      setBookings([]);
      setBookingsInsights(null);
      fetchDatasets();
    } catch (err) { toast.error('Failed to delete'); }
  };

  // Inline cell save
  const saveInlineEdit = async (rowId, field, value, type = 'transaction') => {
    try {
      if (type === 'transaction') {
        await api.patch(`/imported-data/transactions/${rowId}`, { [field]: value });
        fetchRows(selectedDataset.id, rowsPage, rowsSearch);
      } else {
        await api.patch(`/imported-data/bookings/${rowId}/edit`, { [field]: value });
        fetchBookings(selectedDataset.id, bookingsPage, bookingsSearch, bookingsFilter, bookingsStatFilter);
      }
      setEditingCell(null);
      toast.success('Updated');
    } catch { toast.error('Failed to save'); }
  };

  // Edit dialog
  const openEditDialog = (row, type) => {
    setEditDialogData({ ...row });
    setEditDialogType(type);
    setEditDialogOpen(true);
  };

  const saveEditDialog = async () => {
    if (!editDialogData) return;
    try {
      const { id, ...data } = editDialogData;
      if (editDialogType === 'transaction') {
        await api.patch(`/imported-data/transactions/${id}`, data);
        fetchRows(selectedDataset.id, rowsPage, rowsSearch);
      } else {
        await api.patch(`/imported-data/bookings/${id}/edit`, data);
        fetchBookings(selectedDataset.id, bookingsPage, bookingsSearch, bookingsFilter, bookingsStatFilter);
      }
      setEditDialogOpen(false);
      toast.success('Row updated');
    } catch { toast.error('Failed to save'); }
  };

  // Inline editable cell component
  const EditableCell = ({ rowId, field, value, type = 'transaction' }) => {
    const isEditing = editingCell?.rowId === rowId && editingCell?.field === field;
    if (isEditing) {
      return (
        <Input
          autoFocus
          defaultValue={editingCell.value}
          className="h-6 text-xs p-1 min-w-[80px]"
          onBlur={(e) => saveInlineEdit(rowId, field, e.target.value, type)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveInlineEdit(rowId, field, e.target.value, type);
            if (e.key === 'Escape') setEditingCell(null);
          }}
        />
      );
    }
    return (
      <span
        className="cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1 rounded transition-colors"
        onDoubleClick={() => setEditingCell({ rowId, field, value: value ?? '' })}
        title="Double-click to edit"
      >
        {value ?? '-'}
      </span>
    );
  };

  const toggleColumn = async (col) => {
    const newHidden = hiddenColumns.includes(col) ? hiddenColumns.filter(c => c !== col) : [...hiddenColumns, col];
    setHiddenColumns(newHidden);
    try {
      await api.patch(`/imported-data/${selectedDataset.id}/columns`, { hidden_columns: newHidden });
      fetchRows(selectedDataset.id, rowsPage, rowsSearch);
    } catch (err) { toast.error('Failed to update columns'); }
  };

  const handleSearch = (val) => {
    setRowsSearch(val);
    setRowsPage(1);
    if (selectedDataset) fetchRows(selectedDataset.id, 1, val);
  };

  const handlePageChange = (page) => {
    setRowsPage(page);
    if (selectedDataset) fetchRows(selectedDataset.id, page, rowsSearch);
  };

  const s = insights?.summary || {};
  const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Database className="w-6 h-6" /> Imported Data</h1>
          <p className="text-muted-foreground">Upload, view, and analyze transaction data from external platforms</p>
        </div>
        <div className="flex gap-2">
          <Label htmlFor="csv-upload" className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border ${uploading ? 'opacity-50' : 'hover:bg-accent cursor-pointer'}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload CSV
            </div>
          </Label>
          <Input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleUpload} disabled={uploading} />
          <Label htmlFor="xlsx-upload" className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border bg-blue-50 text-blue-700 ${uploading ? 'opacity-50' : 'hover:bg-blue-100 cursor-pointer'}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Upload Bookings (XLSX)
            </div>
          </Label>
          <Input id="xlsx-upload" type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBookingsUpload} disabled={uploading} />
        </div>
      </div>

      {/* Dataset selector */}
      {datasets.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {datasets.map(ds => (
            <button
              key={ds.id}
              onClick={() => selectDataset(ds)}
              className={`flex-shrink-0 p-3 rounded-lg border-2 text-left transition-all ${
                selectedDataset?.id === ds.id ? 'border-primary bg-primary/5 shadow-md' : 'border-transparent bg-white hover:border-gray-200'
              }`}
            >
              <p className="font-semibold text-sm truncate max-w-[200px]">{ds.name}</p>
              <p className="text-xs text-muted-foreground">{ds.row_count?.toLocaleString()} rows · {ds.imported_at?.split('T')[0]}</p>
            </button>
          ))}
        </div>
      )}

      {!selectedDataset && datasets.length === 0 && (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <FileSpreadsheet className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">No data imported yet</p>
          <p className="text-sm">Upload a CSV file to get started with data analysis</p>
        </CardContent></Card>
      )}

      {selectedDataset && (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="insights"><BarChart3 className="h-4 w-4 mr-1" /> Revenue</TabsTrigger>
                <TabsTrigger value="bookings"><Users className="h-4 w-4 mr-1" /> Bookings</TabsTrigger>
                <TabsTrigger value="data"><Database className="h-4 w-4 mr-1" /> Transactions</TabsTrigger>
                <TabsTrigger value="columns"><Eye className="h-4 w-4 mr-1" /> Columns</TabsTrigger>
              </TabsList>
              <Button variant="destructive" size="sm" onClick={() => handleDeleteDataset(selectedDataset.id)}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete Dataset
              </Button>
            </div>

            {/* INSIGHTS TAB */}
            <TabsContent value="insights" className="mt-4 space-y-6">
              {loadingInsights ? (
                <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
              ) : insights && (
                <>
                  {/* Revenue Summary Cards — Total Revenue */}
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Total Revenue (Checkout)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <Card className="border-green-200 bg-green-50/30"><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Total</p>
                        <p className="text-2xl font-bold text-green-600">{fmt(s.total_revenue)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Daily Avg</p>
                        <p className="text-xl font-bold text-blue-600">{fmt(s.daily_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Weekly Avg</p>
                        <p className="text-xl font-bold text-cyan-600">{fmt(s.weekly_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Monthly Avg</p>
                        <p className="text-xl font-bold text-purple-600">{fmt(s.monthly_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Yearly Avg</p>
                        <p className="text-xl font-bold text-indigo-600">{fmt(s.yearly_average)}</p>
                      </CardContent></Card>
                    </div>
                  </div>

                  {/* Revenue Summary Cards — Net Revenue */}
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Net Revenue (After Fees)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <Card className="border-emerald-200 bg-emerald-50/30"><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Total</p>
                        <p className="text-2xl font-bold text-emerald-600">{fmt(s.total_net_revenue)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Daily Avg</p>
                        <p className="text-xl font-bold text-blue-500">{fmt(s.net_daily_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Weekly Avg</p>
                        <p className="text-xl font-bold text-cyan-500">{fmt(s.net_weekly_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Monthly Avg</p>
                        <p className="text-xl font-bold text-purple-500">{fmt(s.net_monthly_average)}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-4 text-center">
                        <p className="text-xs text-muted-foreground">Yearly Avg</p>
                        <p className="text-xl font-bold text-indigo-500">{fmt(s.net_yearly_average)}</p>
                      </CardContent></Card>
                    </div>
                  </div>

                  {/* Secondary Stats */}
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold">{s.total_transactions?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Transactions</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold">{s.unique_customers?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Customers</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold">{s.unique_activities}</p>
                      <p className="text-xs text-muted-foreground">Activities</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold">{s.unique_locations}</p>
                      <p className="text-xs text-muted-foreground">Locations</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-red-600">{fmt(s.total_refunded)}</p>
                      <p className="text-xs text-muted-foreground">Refunded</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-yellow-600">{fmt(s.total_coupon_discounts)}</p>
                      <p className="text-xs text-muted-foreground">Discounts</p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Monthly Revenue Chart — Dual bars */}
                    <Card className="lg:col-span-2">
                      <CardHeader><CardTitle className="text-base">Monthly Revenue — Total vs Net</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={insights.monthly_revenue}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={v => v ? new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : ''} fontSize={11} />
                            <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}k`} fontSize={11} />
                            <Tooltip formatter={(v, name) => [`$${Number(v).toLocaleString('en-US', {minimumFractionDigits: 2})}`, name === 'revenue' ? 'Total Revenue' : 'Net Revenue']} />
                            <Legend formatter={v => v === 'revenue' ? 'Total Revenue' : 'Net Revenue'} />
                            <Bar dataKey="revenue" fill="#10b981" radius={[4,4,0,0]} name="revenue" />
                            <Bar dataKey="net_revenue" fill="#2563eb" radius={[4,4,0,0]} name="net_revenue" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Revenue by Location Pie */}
                    <Card>
                      <CardHeader><CardTitle className="text-base">Revenue by Location</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie data={insights.by_location} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${(percent*100).toFixed(0)}%`} fontSize={10}>
                              {insights.by_location?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={v => `$${Number(v).toLocaleString()}`} />
                            <Legend fontSize={10} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Yearly Revenue */}
                  {insights.yearly_revenue?.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base">Yearly Revenue — Total vs Net</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {insights.yearly_revenue.map(y => (
                            <div key={y.year} className="p-4 bg-gray-50 rounded-lg text-center">
                              <p className="text-lg font-bold">{y.year}</p>
                              <p className="text-sm text-green-600 font-semibold">{fmt(y.revenue)} <span className="text-xs text-muted-foreground">total</span></p>
                              <p className="text-sm text-blue-600 font-semibold">{fmt(y.net_revenue)} <span className="text-xs text-muted-foreground">net</span></p>
                              <p className="text-xs text-muted-foreground">{y.transactions} txns</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Top Activities */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Top Activities by Revenue</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={Math.max(200, (insights.by_activity?.length || 0) * 30)}>
                        <BarChart data={insights.by_activity} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={v => `$${(v/1000).toFixed(0)}k`} fontSize={11} />
                          <YAxis type="category" dataKey="name" width={200} fontSize={10} />
                          <Tooltip formatter={v => [`$${Number(v).toLocaleString('en-US', {minimumFractionDigits: 2})}`, 'Revenue']} />
                          <Bar dataKey="revenue" fill="#2563eb" radius={[0,4,4,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* BOOKINGS TAB */}
            <TabsContent value="bookings" className="mt-4 space-y-6">
              {bookingsInsights ? (() => {
                const bs = bookingsInsights.summary || {};
                const applyStatFilter = (filter) => {
                  const newFilter = bookingsStatFilter === filter ? '' : filter;
                  setBookingsStatFilter(newFilter);
                  setBookingsPage(1);
                  setBookingsFilter('all');
                  fetchBookings(selectedDataset.id, 1, bookingsSearch, 'all', newFilter);
                  // Scroll to table
                  document.getElementById('bookings-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                };
                const isActive = (f) => bookingsStatFilter === f;
                return (
                  <>
                    {/* Top Stats — Clickable */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { key: 'courses_only', label: 'Course Enrollments', count: bs.total_enrollments, color: 'text-blue-600' },
                        { key: 'families', label: 'Unique Families', count: bs.unique_families, color: 'text-green-600' },
                        { key: 'children', label: 'Unique Children', count: bs.unique_children, color: 'text-purple-600' },
                        { key: 'courses_list', label: 'Unique Courses', count: bs.unique_courses, color: 'text-indigo-600' },
                      ].map(card => (
                        <button key={card.key} onClick={() => applyStatFilter(card.key)}
                          className={`p-4 rounded-xl border-2 transition-all text-center ${
                            isActive(card.key) ? 'border-primary bg-primary/5 shadow-md ring-1 ring-primary/20' : 'border-transparent bg-white hover:border-gray-200 hover:shadow-sm'
                          }`}>
                          <p className={`text-2xl font-bold ${isActive(card.key) ? 'text-primary' : card.color}`}>{card.count?.toLocaleString()}</p>
                          <p className={`text-xs font-medium ${isActive(card.key) ? 'text-primary' : 'text-muted-foreground'}`}>{card.label}</p>
                        </button>
                      ))}
                    </div>

                    {/* Secondary Stats — Clickable */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[
                        { key: 'kit_purchases', label: 'Kit Purchases', count: bs.total_kit_purchases, color: 'text-amber-600', bg: 'bg-amber-50' },
                        { key: 'free_trials', label: 'Free Trials', count: bs.free_trials, color: 'text-cyan-600', bg: 'bg-cyan-50' },
                        { key: 'first_bookings', label: 'First Bookings', count: bs.first_bookings, color: 'text-green-600', bg: 'bg-green-50' },
                        { key: 'locations', label: 'Locations', count: bs.unique_locations, color: 'text-gray-700', bg: 'bg-gray-50' },
                      ].map(card => (
                        <button key={card.key} onClick={() => applyStatFilter(card.key)}
                          className={`p-3 rounded-lg text-center transition-all border-2 ${
                            isActive(card.key) ? 'border-primary bg-primary/5 shadow-md' : `border-transparent ${card.bg} hover:border-gray-200`
                          }`}>
                          <p className={`text-lg font-bold ${isActive(card.key) ? 'text-primary' : card.color}`}>{card.count}</p>
                          <p className={`text-xs ${isActive(card.key) ? 'text-primary' : 'text-muted-foreground'}`}>{card.label}</p>
                        </button>
                      ))}
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs font-medium">{bs.first_date} — {bs.last_date}</p>
                        <p className="text-xs text-muted-foreground">Date Range</p>
                      </div>
                    </div>

                    {bookingsStatFilter && (
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                        <Badge className="bg-blue-100 text-blue-700">
                          Filtered: {bookingsStatFilter.replace(/_/g, ' ')}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => { setBookingsStatFilter(''); fetchBookings(selectedDataset.id, 1, bookingsSearch, 'all', ''); }}>
                          Clear filter
                        </Button>
                        <span className="text-sm text-muted-foreground ml-auto">{bookingsTotal} results</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Top Courses */}
                      <Card>
                        <CardHeader><CardTitle className="text-base">Top Courses by Enrollments</CardTitle></CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={Math.max(200, (bookingsInsights.top_courses?.length || 0) * 28)}>
                            <BarChart data={bookingsInsights.top_courses} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" fontSize={11} />
                              <YAxis type="category" dataKey="name" width={180} fontSize={10} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#2563eb" radius={[0,4,4,0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>

                      {/* Kit Purchases */}
                      <Card>
                        <CardHeader><CardTitle className="text-base">Kit Purchases & Course Mapping</CardTitle></CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {bookingsInsights.kit_breakdown?.map((k, i) => (
                              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                                <div>
                                  <span className="font-medium">{k.kit}</span>
                                  <span className="text-muted-foreground ml-2">×{k.count}</span>
                                </div>
                                {k.linked_course ? (
                                  <Badge className="bg-green-100 text-green-700 text-xs">→ {k.linked_course.split('|')[0].trim().substring(0, 30)}</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-gray-400">unmapped</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                );
              })() : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No bookings data for this dataset. Upload a Bookings XLSX file.</p>
                </div>
              )}

              {/* Bookings Table */}
              {bookingsTotal > 0 && (
                <div className="space-y-3" id="bookings-table">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search bookings..." value={bookingsSearch} onChange={e => { setBookingsSearch(e.target.value); setBookingsPage(1); fetchBookings(selectedDataset.id, 1, e.target.value, bookingsFilter, bookingsStatFilter); }} className="pl-10" />
                    </div>
                    <div className="flex gap-1">
                      {['all', 'course', 'kit_purchase'].map(f => (
                        <Button key={f} variant={bookingsFilter === f ? 'default' : 'outline'} size="sm" onClick={() => { setBookingsFilter(f); setBookingsPage(1); fetchBookings(selectedDataset.id, 1, bookingsSearch, f, bookingsStatFilter); }}>
                          {f === 'all' ? 'All' : f === 'course' ? 'Courses' : 'Kit Purchases'}
                        </Button>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">{bookingsTotal} records</span>
                  </div>
                  <div className="overflow-auto border rounded-lg max-h-[400px]">
                    {loadingBookings ? <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs w-8"></TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs">Customer</TableHead>
                            <TableHead className="text-xs">Child</TableHead>
                            <TableHead className="text-xs">Category</TableHead>
                            <TableHead className="text-xs">Course</TableHead>
                            <TableHead className="text-xs">Grade</TableHead>
                            <TableHead className="text-xs">Schedule</TableHead>
                            <TableHead className="text-xs">Location</TableHead>
                            <TableHead className="text-xs">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bookings.map((b, i) => (
                            <TableRow key={b.id || i} className={b.record_type === 'kit_purchase' ? 'bg-amber-50/50' : ''}>
                              <TableCell className="p-1">
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditDialog(b, 'booking')} title="Edit row">
                                  <Edit className="h-3 w-3" />
                                </Button>
                              </TableCell>
                              <TableCell className="text-xs">{b.record_type === 'kit_purchase' ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">Kit</Badge> : <Badge className="bg-blue-100 text-blue-700 text-[10px]">Course</Badge>}</TableCell>
                              <TableCell className="text-xs"><EditableCell rowId={b.id} field="customer_email" value={`${b.first_name} ${b.last_name}`} type="booking" /></TableCell>
                              <TableCell className="text-xs"><EditableCell rowId={b.id} field="child_name" value={b.child_name} type="booking" /></TableCell>
                              <TableCell className="text-xs"><EditableCell rowId={b.id} field="activity_category" value={b.is_kit_purchase ? b.kit_name : b.category} type="booking" /></TableCell>
                              <TableCell className="text-xs font-medium"><EditableCell rowId={b.id} field="activity_course" value={b.is_kit_purchase ? (b.linked_course || '—') : b.course} type="booking" /></TableCell>
                              <TableCell className="text-xs"><EditableCell rowId={b.id} field="activity_grade_range" value={b.grade_range || '-'} type="booking" /></TableCell>
                              <TableCell className="text-xs">{b.schedule}</TableCell>
                              <TableCell className="text-xs truncate max-w-[120px]"><EditableCell rowId={b.id} field="location_name" value={b.location} type="booking" /></TableCell>
                              <TableCell className="text-xs">{b.booking_date || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Page {bookingsPage} of {Math.ceil(bookingsTotal / 50)}</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={bookingsPage <= 1} onClick={() => { setBookingsPage(bookingsPage - 1); fetchBookings(selectedDataset.id, bookingsPage - 1, bookingsSearch, bookingsFilter, bookingsStatFilter); }}><ChevronLeft className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" disabled={bookingsPage >= Math.ceil(bookingsTotal / 50)} onClick={() => { setBookingsPage(bookingsPage + 1); fetchBookings(selectedDataset.id, bookingsPage + 1, bookingsSearch, bookingsFilter, bookingsStatFilter); }}><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* DATA TABLE TAB */}
            <TabsContent value="data" className="mt-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search by name, email, activity..." value={rowsSearch} onChange={e => handleSearch(e.target.value)} className="pl-10" />
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">{rowsTotal.toLocaleString()} rows</span>
              </div>

              <div className="overflow-auto border rounded-lg max-h-[500px]">
                {loadingRows ? (
                  <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {visibleColumns.map(col => (
                          <TableHead key={col} className="text-xs whitespace-nowrap font-semibold">{columnLabels[col] || col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row, i) => (
                        <TableRow key={row.id || i}>
                          {visibleColumns.map(col => (
                            <TableCell key={col} className="text-xs whitespace-nowrap max-w-[200px] truncate">
                              {typeof row[col] === 'number' && col.includes('amount') || col.includes('revenue') || col.includes('fee') || col.includes('discount') || col === 'gross_amount' || col === 'checkout_amount' || col === 'net_revenue' || col === 'cc_fee' || col === 'refunded_amount'
                                ? `$${Number(row[col]).toFixed(2)}`
                                : row[col] ?? '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Page {rowsPage} of {Math.ceil(rowsTotal / 50)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={rowsPage <= 1} onClick={() => handlePageChange(rowsPage - 1)}>
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={rowsPage >= Math.ceil(rowsTotal / 50)} onClick={() => handlePageChange(rowsPage + 1)}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* COLUMNS TAB */}
            <TabsContent value="columns" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Column Visibility</CardTitle>
                  <CardDescription>Toggle columns to show/hide in the data table. Hidden columns are not deleted — data is preserved.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(columnLabels).length > 0 ? Object.entries(columnLabels).map(([col, label]) => {
                      const isHidden = hiddenColumns.includes(col);
                      return (
                        <button
                          key={col}
                          onClick={() => toggleColumn(col)}
                          className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-all ${
                            isHidden ? 'bg-gray-50 text-gray-400 border-gray-200' : 'bg-white text-foreground border-green-200 shadow-sm'
                          }`}
                        >
                          {isHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4 text-green-600" />}
                          <span className={isHidden ? 'line-through' : 'font-medium'}>{label}</span>
                        </button>
                      );
                    }) : (
                      <p className="text-muted-foreground col-span-3">Select a dataset first to manage columns</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editDialogType === 'booking' ? 'Booking' : 'Transaction'} Row</DialogTitle>
          </DialogHeader>
          {editDialogData && (
            <div className="grid grid-cols-2 gap-3 py-2">
              {Object.entries(editDialogData).filter(([k]) => k !== 'id' && k !== 'record_type' && k !== 'is_kit_purchase').map(([key, val]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs font-medium capitalize">{key.replace(/_/g, ' ')}</Label>
                  <Input
                    value={val ?? ''}
                    onChange={e => setEditDialogData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveEditDialog}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
